import { useState, type ReactNode } from 'react'
import { Link, useNavigate, useRouterState } from '@tanstack/react-router'
import { BarChart3, IdCard, Users, Wallet, type LucideIcon } from 'lucide-react'
import { useCustomerAuth } from '@/lib/auth/auth-context'
import { ChangePasswordModal } from '@/components/ChangePasswordModal'
import { GetHelpDrawer } from '@/components/GetHelpDrawer'
import { UserMenu } from '@/components/UserMenu'
import { ATFLogo } from '@/components/assets/ATFLogo'
import { CHARCOAL, DARK as CARD, GOLD, GOLD_DIM, GREY_300, GREY_400, GREY_500, WHITE } from '@/lib/genesis'

// The customer portal's single-account nav (ADR-179 D4: "CustomerLayout
// (single-account nav: Billing · Licenses · Usage)"). Deliberately smaller
// than the operator RootLayout's nav — there is no Fleet/Accounts/Operators
// item, because a CustomerUser's session is forced to exactly one account
// (D2.3: cross-account/fleet views are never reachable from this surface).
const NAV_ITEMS: ReadonlyArray<{ to: string; label: string; icon: LucideIcon }> = [
  { to: '/billing', label: 'Billing', icon: Wallet },
  { to: '/licenses', label: 'Licenses', icon: IdCard },
  { to: '/usage', label: 'Usage', icon: BarChart3 },
  { to: '/members', label: 'Members', icon: Users },
]

// Three-surface UI alignment pass (three-surface-ui-alignment): this shell
// already carried the Genesis charcoal + gold palette from #3337; this
// pass upgrades its LAYOUT to the ui/ app idiom (sidebar structure, glass
// chrome, logo lockup + badge) and moves its color constants to the
// shared apps-future/portal/src/lib/genesis.ts module used by every
// portal route, rather than each file re-declaring its own hex literals.
//
// ui-container-nav-usermenu pass: (1) content container now mirrors ui/'s
// per-route wrapper (max-w-[1200px] mx-auto, 40px/lg horizontal padding)
// instead of a flat p-6 with no max-width; (2) every nav item gets a
// lucide-react icon at w-5 h-5, matching ui/'s Sidebar.tsx icon sizing;
// (3) the footer identity block is replaced by the shared UserMenu
// component (apps-future/portal/src/components/UserMenu.tsx, structurally
// identical to the console's) -- a click-to-open popover chip (avatar +
// name/email) that moves role off the always-visible chip and into the
// popover. WhoAmICustomer has no name field (only userId/accountId/email/
// role -- see apps-future/portal/src/lib/auth/auth-context.tsx's
// CustomerPrincipal), so the chip always falls back to email here; that
// matches the task brief's "name, email fallback" instruction.

export function CustomerLayout({ children }: { children: ReactNode }) {
  const { status, principal, logout } = useCustomerAuth()
  const navigate = useNavigate()
  const pathname = useRouterState({ select: (s) => s.location.pathname })
  const [changingPassword, setChangingPassword] = useState(false)
  const [gettingHelp, setGettingHelp] = useState(false)

  // /accept-invite is UNAUTHENTICATED, same as /login — a fresh invitee
  // has no customer_session cookie yet (ADR-179 P3 / section 7-E).
  if (pathname === '/login' || pathname === '/accept-invite') {
    return <>{children}</>
  }

  if (status === 'checking') {
    return (
      <div
        className="flex h-screen items-center justify-center text-sm"
        style={{ backgroundColor: CHARCOAL, color: GREY_400 }}
      >
        Checking session…
      </div>
    )
  }

  if (status === 'unauthenticated') {
    navigate({ to: '/login' })
    return null
  }

  return (
    <div className="flex min-h-screen" style={{ backgroundColor: CHARCOAL }}>
      {/* Sidebar -- 240px, mirrors ui/'s Sidebar idiom (logo lockup top,
          gold active state, muted-grey inactive) -- same structure as the
          operator RootLayout, distinguished only by the badge copy below
          and the smaller single-account nav (ADR-179 D4). */}
      <aside
        className="fixed top-0 bottom-0 left-0 z-40 flex w-60 flex-col"
        style={{
          background: `linear-gradient(to bottom, ${CARD}, ${CHARCOAL})`,
          borderRight: `1px solid ${GREY_500}`,
        }}
      >
        <div
          className="flex h-[60px] items-center gap-2 px-4"
          style={{ borderBottom: `1px solid ${GREY_500}` }}
        >
          <ATFLogo variant="icon" size="sm" style={{ color: GOLD }} />
          <div className="flex flex-col leading-none">
            <span className="font-display text-sm font-semibold" style={{ color: WHITE }}>
              ATF Account
            </span>
            <span
              className="mt-0.5 inline-flex w-fit items-center rounded-full px-1.5 py-0.5 text-[9px] font-medium uppercase tracking-wide"
              style={{ backgroundColor: GOLD_DIM, color: GOLD }}
            >
              Customer Portal
            </span>
          </div>
        </div>

        <nav className="flex-1 overflow-y-auto p-3">
          <ul role="list" className="m-0 flex list-none flex-col gap-1 p-0">
            {NAV_ITEMS.map((item) => {
              const Icon = item.icon
              return (
                <li key={item.to} role="listitem">
                  <Link
                    to={item.to}
                    className="glass-button flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm transition-colors"
                    style={{ color: GREY_300 }}
                    activeProps={{
                      style: { color: GOLD, backgroundColor: GOLD_DIM, fontWeight: 500 },
                    }}
                  >
                    <Icon className="h-5 w-5 flex-shrink-0" aria-hidden="true" />
                    <span className="truncate">{item.label}</span>
                  </Link>
                </li>
              )
            })}
          </ul>
        </nav>

        {/* Footer -- congruent user-menu pattern across all three UI
            services (operator feedback): avatar + name/email chip, role
            moved into the click-to-open popover instead of shown by
            default. */}
        <div className="p-3" style={{ borderTop: `1px solid ${GREY_500}` }}>
          {principal && (
            <UserMenu
              email={principal.email}
              roleLabel={
                principal.role === 'owner'
                  ? 'Owner'
                  : principal.role === 'admin'
                    ? 'Admin'
                    : 'Member'
              }
              onChangePassword={() => setChangingPassword(true)}
              onGetHelp={() => setGettingHelp(true)}
              onSignOut={() => {
                void logout()
              }}
            />
          )}
        </div>
      </aside>
      <main className="atf-grid-bg ml-60 flex-1 overflow-y-auto" style={{ backgroundColor: CHARCOAL }}>
        <div className="min-h-screen px-4 pt-6 pb-6 lg:px-10">
          <div className="mx-auto max-w-[1200px]">{children}</div>
        </div>
      </main>
      {changingPassword && (
        <ChangePasswordModal onClose={() => setChangingPassword(false)} />
      )}
      {gettingHelp && <GetHelpDrawer onClose={() => setGettingHelp(false)} />}
    </div>
  )
}
